<template>
  <div class="be-location" v-if="hasAnyContent" :style="resolvedFontVars">
    <h2 class="be-location__title mb-4">Location</h2>

    <div class="row">
      <div class="col-12 col-lg-7 mb-4 mb-lg-0" v-if="hasMapEmbed">
        <div class="be-location__map-card">
          <iframe
            :src="locationEmbedMap"
            width="100%"
            height="260"
            style="border: 0"
            class="be-location__map"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"></iframe>

          <div
            class="be-location__map-info"
            :class="{ 'be-location__map-info--clickable': hasAddress || hasLocationDescription }"
            @click="isModalOpen = true">
            <div class="be-location__address" v-if="hasAddress">
              <MapPin :size="16" class="be-location__address-icon" />
              <span>{{ getMerchant.address }}</span>
            </div>
            <p class="be-location__description" v-if="hasLocationDescription">
              {{ getMerchant.location_description }}
            </p>
          </div>
        </div>
      </div>

      <div class="col-12" :class="hasMapEmbed ? 'col-lg-5' : 'col-lg-12'" v-if="hasNearbyList">
        <div class="be-location__nearby">
          <h3 class="be-location__nearby-title">Nearby Destinations</h3>

          <div class="be-location__tabs hide-scroll">
            <button
              type="button"
              class="be-location__tab"
              :class="{ 'be-location__tab--active': selectedCategoryId === tab.id }"
              v-for="tab in categoryTabs"
              :key="tab.id"
              @click="selectedCategoryId = tab.id">
              {{ tab.name }} ({{ tab.count }})
            </button>
          </div>

          <div class="be-location__list">
            <div v-for="place in previewList" :key="place.id" class="be-location__item">
              <img
                v-if="place.place_category && place.place_category.image_url"
                :src="place.place_category.image_url"
                :alt="place.place_category.name"
                class="be-location__item-icon" />
              <div class="be-location__item-info">
                <p class="be-location__item-name">{{ place.name }}</p>
                <p class="be-location__item-category">{{ place.place_category && place.place_category.name }}</p>
                <p class="be-location__item-distance">
                  <span class="be-location__item-distance-value">{{ place.distance }}</span>
                  from the property
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="be-location__view-all"
            v-if="filteredList.length > previewList.length"
            @click="isModalOpen = true">
            View all nearby destinations
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>

    <BeLocationNearbyModal
      :is-open="isModalOpen"
      :merchant="getMerchant"
      :nearby-list="nearby.list"
      :font-vars="resolvedFontVars"
      @close="isModalOpen = false" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { get } from '@/services/index.js';
import { MapPin, ChevronRight } from 'lucide-vue';
import { getMerchantMapEmbedUrl } from '@/utils/merchantLocation.js';
import BeLocationNearbyModal from '@/components/hotel/be/v3/BeLocationNearbyModal.vue';

const NEAREST_TAB_ID = 'nearest';
const PREVIEW_LIMIT = 3;

export default {
  name: 'BeLocationNearby',
  components: {
    MapPin,
    ChevronRight,
    BeLocationNearbyModal,
  },
  data() {
    return {
      nearby: {
        list: [],
        isLoad: false,
      },
      selectedCategoryId: NEAREST_TAB_ID,
      isModalOpen: false,
      // Whether this instance sits under SearchRoomLandingPage's .be-landing (and
      // so inherits its --be-v3-*-font) can only be read from the actual rendered
      // DOM, since the same component/data is reused on plain pages too. Also
      // carries a title font-weight: v3's title font (Cormorant Garamond by
      // default) reads thin next to a heavy weight, while the legacy pages'
      // inherited sans reads too heavy at that same weight -- so the two need
      // different numbers, not just different families.
      // BeLocationNearbyModal is additionally teleported to <body> by
      // bootstrap-vue (BVTransporter), breaking CSS inheritance entirely, so it
      // needs these values restated inline -- passed down via the font-vars prop.
      resolvedFontVars: {},
    };
  },
  computed: {
    ...mapGetters(['getMerchant']),
    locationEmbedMap() {
      return getMerchantMapEmbedUrl(this.getMerchant);
    },
    hasMapEmbed() {
      return this.$isNotEmptyV2(this.locationEmbedMap);
    },
    hasAddress() {
      return this.$isNotEmptyV2(this.getMerchant.address);
    },
    hasLocationDescription() {
      return this.$isNotEmptyV2(this.getMerchant.location_description);
    },
    hasNearbyList() {
      return this.nearby.list.length > 0;
    },
    hasAnyContent() {
      return this.hasMapEmbed || this.hasNearbyList;
    },
    categoryTabs() {
      const categories = [];
      this.nearby.list.forEach((place) => {
        const category = place.place_category;
        if (!category) return;
        const existing = categories.find((item) => item.id === category.id);
        if (existing) {
          existing.count += 1;
        } else {
          categories.push({ id: category.id, name: category.name, count: 1 });
        }
      });
      return [{ id: NEAREST_TAB_ID, name: 'Nearest', count: this.nearby.list.length }, ...categories];
    },
    filteredList() {
      if (this.selectedCategoryId === NEAREST_TAB_ID) return this.nearby.list;
      return this.nearby.list.filter(
        (place) => place.place_category && place.place_category.id === this.selectedCategoryId
      );
    },
    previewList() {
      return this.filteredList.slice(0, PREVIEW_LIMIT);
    },
  },
  watch: {
    hasAnyContent(isVisible) {
      if (isVisible) this.$nextTick(this.resolveFontVars);
    },
  },
  mounted() {
    this.loadNearbyPlaces();
    if (this.hasAnyContent) this.$nextTick(this.resolveFontVars);
  },
  methods: {
    resolveFontVars() {
      if (!this.$el || !this.$el.nodeType || this.$el.nodeType !== Node.ELEMENT_NODE) return;
      const styles = getComputedStyle(this.$el);
      const contentFont = styles.getPropertyValue('--be-v3-content-font').trim();
      const titleFont = styles.getPropertyValue('--be-v3-title-font').trim();
      const isBeLanding = Boolean(titleFont);
      this.resolvedFontVars = {
        ...(contentFont ? { '--be-v3-content-font': contentFont } : {}),
        ...(titleFont ? { '--be-v3-title-font': titleFont } : {}),
        '--be-location-title-weight': isBeLanding ? '700' : '500',
        // v3's content font (Jost) is capped at weight 500, so 700 there renders
        // as that lighter capped face -- the legacy pages' inherited font has a
        // real 700 face, which reads noticeably heavier at the same number.
        '--be-location-bold-weight': isBeLanding ? '700' : '600',
      };
    },
    async loadNearbyPlaces() {
      try {
        this.nearby.isLoad = true;
        const apiResponse = await get('CORE_HOTEL', 'merchant-nearby-place', {
          merchant_id: this.getMerchant.id,
          keyword: '',
        });
        const { success, data = [] } = apiResponse.data;
        this.nearby.list = success ? data : [];
      } catch (error) {
        this.nearby.list = [];
        console.log('ERROR BeLocationNearby loadNearbyPlaces :', error.message || error);
      } finally {
        this.nearby.isLoad = false;
      }
    },
  },
};
</script>

<style scoped>
/* Only SearchRoomLandingPage's .be-landing ancestor sets --be-v3-*-font (to the
   merchant's custom font, or the v3 default if none) -- an explicit `inherit`
   fallback means this component picks that up there, and falls through to each
   OTHER host page's own ambient font (RoomDesktop/RoomMobile/
   SearchRoomWithMultiImageBanner) when the var is unset, instead of forcing the
   v3 font onto pages that don't use v3 styling. */
.be-location {
  font-family: var(--be-v3-content-font, inherit);
}
.be-location__title {
  font-family: var(--be-v3-title-font, inherit);
  font-weight: var(--be-location-title-weight, 500);
  color: #1a1a2e;
  font-size: 22px;
}
.be-location__map {
  width: 100%;
  display: block;
}
.be-location__address {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1a1a2e;
  font-size: 15px;
  font-weight: var(--be-location-bold-weight, 600);
}
.be-location__address-icon {
  flex-shrink: 0;
  color: #999;
}
.be-location__description {
  margin: 10px 0 0;
  color: #555;
  font-size: 14px;
  line-height: 1.6;
}
.be-location__map-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: var(--be-v3-radius, 14px);
  overflow: hidden;
}
.be-location__map-info {
  padding: 16px;
}
.be-location__map-info--clickable {
  cursor: pointer;
}
.be-location__nearby {
  height: 100%;
}
.be-location__nearby-title {
  margin: 0 0 14px;
  font-family: var(--be-v3-title-font, inherit);
  font-size: 1.125rem;
  font-weight: var(--be-location-title-weight, 500);
  color: #1a1a2e;
}
.be-location__tabs {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  margin-bottom: 16px;
}
.be-location__tab {
  flex-shrink: 0;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
  cursor: pointer;
}
.be-location__tab--active {
  color: var(--merchant-primary-color, #79a33a);
  border-color: var(--merchant-primary-color, #79a33a);
  background: color-mix(in srgb, var(--merchant-primary-color, #79a33a) 10%, #fff);
}
.be-location__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.be-location__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.be-location__item-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.be-location__item-name {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: var(--be-location-bold-weight, 600);
  color: #1a1a2e;
}
.be-location__item-category {
  margin: 0 0 4px;
  font-size: 13px;
  color: #999;
}
.be-location__item-distance {
  margin: 0;
  font-size: 13px;
  color: #777;
}
.be-location__item-distance-value {
  font-weight: var(--be-location-bold-weight, 600);
  color: #1a1a2e;
}
.be-location__view-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 18px;
  padding: 0;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: var(--be-location-bold-weight, 600);
  color: #1a1a2e;
  cursor: pointer;
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeLocationNearby.vue